// src/pages/battle/components/BattleGrid.jsx
import React from 'react';
import { Shield } from 'lucide-react';
import { TileType, UnitType } from '../../../utils/combatSystem';
import { GRID_SIZE } from '../../../utils/mapGeneration';
import { TILE_SIZE } from '../Battlemap.constants';
import { getHealthBarColor } from '../Battlemap.utils';
// ✨ NEW: Import sprite components
import { UnitSprite, TileSprite } from './PixelSpriteIntegration';

const BattleGrid = ({
  map,
  units,
  selectedUnit,
  turn,
  handleTileClick,
  getUnitAt,
  primaryColor,
  alertColor,
  theme = 'vanilla' // ✨ NEW: Theme prop with default
}) => {
  const [zoom, setZoom] = React.useState(1);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = React.useState(false);
  const dragTimeoutRef = React.useRef(null);
  const scrollRef = React.useRef(null);
  
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 2));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleZoomReset = () => setZoom(1);
  
  // Mouse wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.5, Math.min(2, prev + delta)));
  };
  
  // Drag to scroll
  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Only left click
    setHasMoved(false);
    setDragStart({
      x: e.clientX + scrollRef.current.scrollLeft,
      y: e.clientY + scrollRef.current.scrollTop
    });
    
    // Only enable dragging after holding for 150ms
    dragTimeoutRef.current = setTimeout(() => {
      setIsDragging(true);
    }, 150);
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setHasMoved(true);
    scrollRef.current.scrollLeft = dragStart.x - e.clientX;
    scrollRef.current.scrollTop = dragStart.y - e.clientY;
  };
  
  const handleMouseUp = () => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    setIsDragging(false);
    // Reset hasMoved after a brief delay to let click handlers fire
    setTimeout(() => setHasMoved(false), 0);
  };
  
  const handleMouseLeave = () => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    setIsDragging(false);
  };
  
  React.useEffect(() => {
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('wheel', handleWheel, { passive: false });
      return () => scrollEl.removeEventListener('wheel', handleWheel);
    }
  }, []);
  
  return (
    <div className="bg-gray-900 rounded-lg p-4 relative">
      {/* Zoom Controls */}
      <div className="absolute top-2 right-2 z-10 flex gap-2 bg-gray-800/90 rounded-lg p-2">
        <button
          onClick={handleZoomOut}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm font-medium transition-colors"
          disabled={zoom <= 0.5}
        >
          −
        </button>
        <button
          onClick={handleZoomReset}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm font-medium transition-colors"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          onClick={handleZoomIn}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm font-medium transition-colors"
          disabled={zoom >= 2}
        >
          +
        </button>
      </div>
      
      <div 
        ref={scrollRef}
        className={`overflow-auto max-h-[600px] ${hasMoved ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div 
          style={{ 
            width: GRID_SIZE * TILE_SIZE * zoom, 
            height: GRID_SIZE * TILE_SIZE * zoom,
            transition: isDragging ? 'none' : 'all 0.2s ease'
          }}
        >
          {Array(GRID_SIZE).fill(null).map((_, y) => (
            <div key={y} className="flex">
              {Array(GRID_SIZE).fill(null).map((_, x) => {
                const unit = getUnitAt(x, y);
                const tileType = map[y][x];
                const isSelected = selectedUnit?.x === x && selectedUnit?.y === y;
                
                return (
                  <div
                    key={`${x}-${y}`}
                    onClick={(e) => {
                      // Only trigger tile click if we didn't drag
                      if (!hasMoved && turn === UnitType.SOLDIER) {
                        handleTileClick(x, y);
                      }
                    }}
                    className={`relative border border-gray-800 ${
                      turn === UnitType.SOLDIER 
                        ? 'cursor-pointer hover:bg-gray-700/50' 
                        : 'cursor-not-allowed'
                    } transition-colors ${isSelected ? 'ring-2 ring-yellow-400' : ''}`}
                    style={{ 
                      width: TILE_SIZE * zoom, 
                      height: TILE_SIZE * zoom,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* ✨ NEW: Tile Sprites (cover terrain) */}
                    <TileSprite 
                      tileType={tileType}
                      theme={theme}
                      size={TILE_SIZE * zoom}
                      className="absolute inset-0"
                    />
                    
                    {/* Cover Indicators (fallback visual) */}
                    {tileType === TileType.FULL_COVER && (
                      <div className="absolute inset-0 bg-gray-600 flex items-center justify-center">
                        <Shield size={20 * zoom} className="text-gray-400" />
                      </div>
                    )}
                    {tileType === TileType.HALF_COVER && (
                      <div className="absolute inset-0 bg-gray-700 flex items-center justify-center">
                        <Shield size={16 * zoom} className="text-gray-500" />
                      </div>
                    )}
                    
                    {/* Unit */}
                    {unit && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="flex-1 flex items-center justify-center">
                          {/* ✨ NEW: UnitSprite with dynamic CSS classes */}
                          <UnitSprite
                            unit={unit}
                            theme={theme}
                            size={32 * zoom}
                            primaryColor={primaryColor}
                            alertColor={alertColor}
                            className={`
                              sprite-hoverable
                              ${isSelected ? 'sprite-highlighted' : ''}
                              ${turn === unit.type ? 'sprite-active-turn' : ''}
                              ${unit.hp <= 20 ? 'sprite-critical' : ''}
                            `}
                          />
                        </div>
                        
                        {/* HP and AP Bars */}
                        <div className="w-full px-1 pb-1 space-y-0.5">
                          <div className="w-full bg-gray-900/80 rounded-full h-1 overflow-hidden">
                            <div 
                              className={`h-full transition-all ${getHealthBarColor(unit.hp, unit.maxHp)}`}
                              style={{ width: `${(unit.hp / unit.maxHp) * 100}%` }}
                            />
                          </div>
                          <div className="w-full bg-gray-900/80 rounded-full h-1 overflow-hidden">
                            <div 
                              className="h-full bg-cyan-400 transition-all"
                              style={{ width: `${(unit.ap / unit.maxAp) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BattleGrid;